import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { TotpAuthGate } from '@lemontaigu/platform-ui';
import { supabase } from '../supabase.js';

/**
 * PMS gate: authenticated → TOTP (AAL2) → membership, which is EITHER
 *   • staff (`public.staff`): approved employees get an individually-provisioned account (RUNBOOK §1:
 *     admin-provisioned, no public sign-up); the operator's bos@ account carries role 'admin', which passes
 *     `is_staff()` too. RLS still differentiates — reservation writes are any approved employee, reference-data
 *     writes (room categories, rate plans) stay admin-only (PMS-BRIEF §5.3);
 *   • or an INSPECTOR of the tax administration (P3 F4c-3, brief §11.17 Q12): no staff row, a time-limited
 *     `pms.fiscal.audit` grant opened from bos.*. Same bar — password + TOTP — and the app shows such a user
 *     « Administration fiscale » alone (`useAccessKind`). RLS is what actually limits them; the narrow nav is the
 *     courtesy of not offering what would come back empty.
 *
 * Flow shared via @lemontaigu/platform-ui (ladder step 4, F8).
 */
export type AccessKind = 'staff' | 'inspector';

const AccessKindContext = createContext<AccessKind>('staff');

/** Who signed in: staff (the whole PMS) or an inspector (« Administration fiscale » only). */
export function useAccessKind(): AccessKind {
  return useContext(AccessKindContext);
}

async function userIsStaff(): Promise<boolean> {
  const { data, error } = await supabase.from('staff').select('user_id').limit(1);
  if (error) return false;
  return (data?.length ?? 0) > 0;
}

async function userHoldsFiscalAudit(): Promise<boolean> {
  const { data, error } = await supabase.rpc('my_capabilities');
  if (error || !Array.isArray(data)) return false;
  return (data as string[]).includes('pms.fiscal.audit');
}

export function AuthGate({ children }: { children: ReactNode }) {
  const [kind, setKind] = useState<AccessKind>('staff');
  const checkMembership = useCallback(async () => {
    if (await userIsStaff()) {
      setKind('staff');
      return true;
    }
    if (await userHoldsFiscalAudit()) {
      setKind('inspector');
      return true;
    }
    return false;
  }, []);
  return (
    <TotpAuthGate
      client={supabase}
      brand="PMS"
      checkMembership={checkMembership}
      deniedMessage="Ce compte n’a pas accès au PMS. Contactez l’administrateur pour une autorisation."
    >
      <AccessKindContext.Provider value={kind}>{children}</AccessKindContext.Provider>
    </TotpAuthGate>
  );
}
